(() => {
  class NutritionalComparison {
    constructor(root) {
      this.root = root;
      this.groupButtons = [...root.querySelectorAll('[data-nutrition-group-button]')];
      this.groupPanels = [...root.querySelectorAll('[data-nutrition-group-panel]')];
      this.modeButtons = [...root.querySelectorAll('[data-nutrition-mode]')];
      this.mode = 'perserve';
      this.sortState = new WeakMap();
      this.bindEvents();
      this.sortRowsInitially();
      this.applyInitialMode();
    }

    bindEvents() {
      this.groupButtons.forEach(button => button.addEventListener('click', () => this.setGroup(button.dataset.nutritionGroupButton)));
      this.modeButtons.forEach(button => button.addEventListener('click', () => this.setMode(button.dataset.nutritionMode)));
      this.groupPanels.forEach(panel => {
        panel.querySelectorAll('[data-nutrition-sort]').forEach(button => {
          button.addEventListener('click', () => this.sortByColumn(panel, Number(button.dataset.nutritionSort), button.dataset.sortType));
        });
      });
    }

    sortRowsInitially() {
      this.groupPanels.forEach(panel => {
        const tbody = panel.querySelector('tbody');
        if (!tbody) return;
        [...tbody.querySelectorAll('tr[data-nutrition-row]')]
          .sort((a, b) => (a.dataset.sortName || '').localeCompare(b.dataset.sortName || '', undefined, { sensitivity: 'base', numeric: true }))
          .forEach(row => tbody.appendChild(row));
        this.sortState.set(panel, { index: 0, direction: 'ascending', type: 'text' });
      });
    }

    applyInitialMode() {
      const active = this.modeButtons.find(button => button.classList.contains('is-active')) || this.modeButtons[0];
      if (active) this.setMode(active.dataset.nutritionMode, false);
    }

    setGroup(groupId) {
      this.groupButtons.forEach(button => {
        const active = button.dataset.nutritionGroupButton === groupId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      this.groupPanels.forEach(panel => {
        const active = panel.dataset.nutritionGroupPanel === groupId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    static cellValue(cell, mode) {
      if (!cell) return null;
      const raw = mode === 'perserve' ? cell.dataset.perserve : cell.dataset.per100;
      const value = Number(raw);
      return raw == null || raw.trim() === '' || !Number.isFinite(value) ? null : value;
    }

    numericValue(row, index) {
      if (index === 1) {
        const value = row.cells[1]?.dataset.nutritionServingSize;
        return value != null && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
      }
      const cell = row.cells[index];
      if (!cell) return null;
      const per100 = NutritionalComparison.cellValue(cell, 'per100');
      if (per100 === null) return null;
      if (this.mode === 'per100') return per100;
      if (this.mode === 'perserve') return NutritionalComparison.cellValue(cell, 'perserve');
      const proteinRaw = row.dataset.proteinPer100;
      const protein = proteinRaw != null && proteinRaw.trim() !== '' ? Number(proteinRaw) : NaN;
      return Number.isFinite(protein) && protein > 0 ? per100 * 26 / protein : null;
    }

    sortByColumn(panel, index, type, forceDirection = null) {
      const tbody = panel.querySelector('tbody');
      if (!tbody) return;
      const previous = this.sortState.get(panel);
      const direction = forceDirection || (previous && previous.index === index && previous.direction === 'ascending' ? 'descending' : 'ascending');
      const rows = [...tbody.querySelectorAll('tr[data-nutrition-row]')];
      rows.sort((a, b) => {
        if (type === 'text') {
          const result = (a.dataset.sortName || '').localeCompare(b.dataset.sortName || '', undefined, { sensitivity: 'base', numeric: true });
          return direction === 'ascending' ? result : -result;
        }
        const av = this.numericValue(a, index), bv = this.numericValue(b, index);
        if (av === null && bv === null) return (a.dataset.sortName || '').localeCompare(b.dataset.sortName || '');
        if (av === null) return 1;
        if (bv === null) return -1;
        const result = av - bv;
        return result === 0 ? (a.dataset.sortName || '').localeCompare(b.dataset.sortName || '') : (direction === 'ascending' ? result : -result);
      });
      rows.forEach(row => tbody.appendChild(row));
      panel.querySelectorAll('thead th').forEach(th => th.removeAttribute('aria-sort'));
      const heading = panel.querySelector(`thead [data-nutrition-sort="${index}"]`)?.closest('th');
      if (heading) heading.setAttribute('aria-sort', direction);
      this.sortState.set(panel, { index, direction, type });
    }

    setMode(mode, emit = true) {
      if (!['per100', 'perserve', 'per26protein'].includes(mode)) return;
      this.mode = mode;
      this.modeButtons.forEach(button => {
        const active = button.dataset.nutritionMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.root.querySelectorAll('tr[data-nutrition-row]').forEach(row => {
        row.querySelectorAll('[data-nutrition-value]').forEach(cell => {
          const index = cell.cellIndex;
          const value = this.numericValue(row, index);
          if (value === null || !Number.isFinite(value)) {
            cell.innerHTML = '<span aria-label="Not available">—</span>';
            return;
          }
          const decimals = Math.max(0, Math.min(2, Number.parseInt(cell.dataset.decimals || '1', 10) || 0));
          const fixed = value.toFixed(decimals);
          const clean = decimals ? fixed.replace(/\.0+$/, '').replace(/(\.\d*?[1-9])0+$/, '$1') : fixed;
          cell.textContent = cell.dataset.unit ? `${clean} ${cell.dataset.unit}` : clean;
        });
      });
      this.groupPanels.forEach(panel => {
        const state = this.sortState.get(panel);
        if (state && state.index > 1) this.sortByColumn(panel, state.index, state.type, state.direction);
      });
      if (emit) this.root.dispatchEvent(new CustomEvent('nutrition:modechange', { detail: { mode } }));
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-nutrition-comparison]').forEach(root => {
      if (root.dataset.nutritionInitialized === 'true') return;
      root.dataset.nutritionInitialized = 'true';
      new NutritionalComparison(root);
    });
  };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
})();